import React, { useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import MentionExtensionBase from '@tiptap/extension-mention'
import { Slice } from '@tiptap/pm/model'
import { Plugin } from '@tiptap/pm/state'
import { mergeAttributes, NodeViewWrapper, ReactNodeViewRenderer, ReactRenderer } from '@tiptap/react'
import partition from 'lodash/partition'
import { useDebounce } from 'usehooks-ts'

import UserOutlinedIcon from '@ant-design/icons/UserOutlined'
import MedicalServiceIcon from '@icons/MedicalServiceIcon'

import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Typography from '@components/_mui/Typography'

import API from '@shared/services/src/API'
import { QK, userRoleToLabel } from '@shared/utils'

import Avatar from '@components/Avatar'
import { UserCardPopper } from '@components/UserCard'

const MentionType = {
  User: 'user',
  Pharmacy: 'pharmacy',
  CareTeam: 'careteam',
  Provider: 'provider',
  Support: 'support',
  Assistant: 'assistant',
}

const styles = {
  mention: {
    display: 'inline-flex',
    px: 0.5,
    borderRadius: 1,
  },
  list: {
    width: '100%',
    maxWidth: 400,
  },
  subheader: {
    lineHeight: (theme) => theme.spacing(4),
    borderBottom: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  listItem: {
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  emptyState: {
    py: 2,
    px: 3,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 0.5,
  },
  loadingState: {
    p: 2,
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
  },
}

// Custom node view component
const Mention = (props) => {
  const { id, label, type } = props.node.attrs

  return (
    <NodeViewWrapper style={{ display: 'inline' }}>
      <Box component="span" sx={[styles.mention, { backgroundColor: type === MentionType.User ? 'primary.light' : 'warning.light' }]}>
        {type === MentionType.User && (
          <UserCardPopper userId={id}>
            <Typography component="span" sx={{ color: 'primary.dark' }}>
              @{label}
            </Typography>
          </UserCardPopper>
        )}
        {[MentionType.Pharmacy, MentionType.CareTeam].includes(type) && (
          <Typography component="span" sx={{ color: 'warning.dark' }}>
            @{label}
          </Typography>
        )}
        {[MentionType.Provider, MentionType.Support, MentionType.Assistant].includes(type) && (
          <UserCardPopper userId={id}>
            <Typography component="span" sx={{ color: 'warning.dark' }}>
              @{label}
            </Typography>
          </UserCardPopper>
        )}
      </Box>
    </NodeViewWrapper>
  )
}

// Custom mention popup component
const UsersList = React.forwardRef((props, ref) => {
  const { clientRect, command, query: search, conversationId, providerId, assistantId, supportId } = props

  const [selectedIndex, setSelectedIndex] = useState(0)
  const referenceEl = useMemo(() => (clientRect ? { getBoundingClientRect: clientRect } : null), [clientRect])
  const debouncedSearch = useDebounce(search, 300)

  const query = { term: debouncedSearch, limit: 6 }

  const { data, isLoading } = useQuery({
    queryKey: QK.conversations.id(conversationId).users.search.list(query),
    queryFn: () => API.conversations.id(conversationId).users.search(query),
    enabled: Boolean(conversationId) && conversationId !== 'new',
    placeholderData: keepPreviousData,
    select: (users) => {
      const utils = []
      if (MentionType.Pharmacy.startsWith(debouncedSearch)) {
        utils.push({ id: MentionType.Pharmacy, label: MentionType.Pharmacy, type: MentionType.Pharmacy })
      }
      if (providerId && MentionType.Provider.startsWith(debouncedSearch)) {
        utils.push({ id: providerId, label: MentionType.Provider, type: MentionType.Provider })
      }
      if (assistantId && MentionType.Assistant.startsWith(debouncedSearch)) {
        utils.push({ id: assistantId, label: MentionType.Assistant, type: MentionType.Assistant })
      }
      if (supportId && MentionType.Support.startsWith(debouncedSearch)) {
        utils.push({ id: supportId, label: MentionType.Support, type: MentionType.Support })
      }
      return [
        ...utils,
        ...users.map((user) => ({
          id: user.id,
          label: user.fullName,
          type: MentionType.User,
          data: user,
        })),
      ]
    },
  })

  // Reset selectedIndex when data changes
  useEffect(() => setSelectedIndex(0), [data])

  const selectItem = (index) => {
    const item = data?.[index]
    if (item) command(item)
  }

  const upHandler = () => setSelectedIndex((selectedIndex + data?.length - 1) % data?.length)
  const downHandler = () => setSelectedIndex((selectedIndex + 1) % data?.length)
  const enterHandler = () => selectItem(selectedIndex)

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler()
        return true
      }

      if (event.key === 'ArrowDown') {
        downHandler()
        return true
      }

      if (event.key === 'Enter') {
        enterHandler()
        return true
      }

      return false
    },
  }))

  const [userItems, groupItems] = partition(data || [], (item) => item.type === MentionType.User)
  const hasResults = (data?.length || 0) > 0

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={styles.loadingState}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Loading suggestions...
          </Typography>
        </Box>
      )
    }

    if (!hasResults) {
      return (
        <Box sx={styles.emptyState}>
          <Typography variant="body2" sx={{ color: 'text.secondary ' }}>
            No results found for "{debouncedSearch}"
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Try a different name or keyword
          </Typography>
        </Box>
      )
    }

    return (
      <List dense disablePadding sx={styles.list} role="listbox" aria-label="Mention suggestions">
        {groupItems.length > 0 && (
          <>
            <ListSubheader disableSticky sx={styles.subheader}>
              Patient Contacts
            </ListSubheader>
            {groupItems.map(({ id, label, type }, index) => (
              <ListItemButton
                key={`group-${id}`}
                divider
                selected={selectedIndex === index}
                onClick={() => selectItem(index)}
                sx={styles.listItem}
                role="option"
                aria-selected={selectedIndex === index}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {type === MentionType.Pharmacy && <MedicalServiceIcon color="warning" style={{ fontSize: 20 }} />}
                  {[MentionType.Provider, MentionType.Assistant, MentionType.Support].includes(type) && (
                    <UserOutlinedIcon color="warning" style={{ fontSize: 16 }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={`@${label}`}
                  slotProps={{
                    primary: { variant: 'h4', fontWeight: 'normal', sx: { color: 'warning.dark' } },
                  }}
                />
              </ListItemButton>
            ))}
          </>
        )}

        {userItems.length > 0 && (
          <>
            <ListSubheader disableSticky sx={styles.subheader}>
              Users
            </ListSubheader>
            {userItems.map(({ id, data }, index) => {
              const actualIndex = groupItems.length + index
              return (
                <ListItemButton
                  key={`user-${id}`}
                  divider
                  selected={selectedIndex === actualIndex}
                  onClick={() => selectItem(actualIndex)}
                  sx={styles.listItem}
                  role="option"
                  aria-selected={selectedIndex === actualIndex}
                >
                  <Avatar hover="card" user={data} size="md" />
                  <ListItemText
                    primary={data.fullName}
                    secondary={userRoleToLabel[data.role]}
                    sx={{ pl: 1, my: 0 }}
                    slotProps={{
                      primary: { lineHeight: 1.5, color: 'primary.main' },
                      secondary: { lineHeight: 1.5 },
                    }}
                  />
                </ListItemButton>
              )
            })}
          </>
        )}
      </List>
    )
  }

  return (
    <Popper id="mentions-popup" placement="top-start" open={Boolean(clientRect)} anchorEl={referenceEl} sx={{ zIndex: 1300 }}>
      <Paper elevation={4} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {renderContent()}
      </Paper>
    </Popper>
  )
})

// Extend and configure the mention extension
const MentionExtension = (conversationId, { providerId, assistantId, supportId } = {}) => {
  const renderUsersPopup = () => {
    let component

    return {
      onStart: (props) => {
        component = new ReactRenderer(UsersList, {
          props: { ...props, conversationId, providerId, assistantId, supportId },
          editor: props.editor,
        })
      },

      onUpdate(props) {
        component?.updateProps({ ...props, conversationId, providerId, assistantId, supportId })
      },

      onKeyDown(props) {
        if (props.event.key === 'Escape') {
          component?.destroy()
          return true
        }

        return component.ref?.onKeyDown(props)
      },

      onExit() {
        component?.destroy()
      },
    }
  }

  return MentionExtensionBase.extend({
    addNodeView() {
      return ReactNodeViewRenderer(Mention)
    },
    addProseMirrorPlugins() {
      return [
        ...(this.parent?.() ?? []),
        new Plugin({
          props: {
            transformPasted: (slice, view) => {
              if (!slice.size) return slice
              return Slice.fromJSON(view.state.schema, resolveRoleMentionNodes(slice.toJSON(), { providerId, assistantId, supportId }))
            },
          },
        }),
      ]
    },
    parseHTML() {
      return [{ tag: 'mention-component' }]
    },
    renderHTML({ HTMLAttributes }) {
      return ['mention-component', mergeAttributes(HTMLAttributes)]
    },
    addAttributes() {
      return {
        id: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-id'),
          renderHTML: (attributes) => {
            if (!attributes.id) return {}
            return { 'data-id': attributes.id }
          },
        },
        label: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-label'),
          renderHTML: (attributes) => {
            if (!attributes.label) return {}
            return { 'data-label': attributes.label }
          },
        },
        type: {
          default: MentionType.User,
          parseHTML: (element) => element.getAttribute('data-type'),
          renderHTML: (attributes) => {
            if (!attributes.type) return {}
            return { 'data-type': attributes.type }
          },
        },
      }
    },
  }).configure({
    deleteTriggerWithBackspace: true,
    suggestion: { allowSpaces: true, render: renderUsersPopup },
  })
}

const resolveRoleMentionNodes = (node, { providerId, assistantId, supportId } = {}) => {
  const roleIds = { [MentionType.Provider]: providerId, [MentionType.Assistant]: assistantId, [MentionType.Support]: supportId }

  const resolve = (item) => {
    if (item.type === 'mention' && item.attrs?.type in roleIds) {
      const id = roleIds[item.attrs.type]
      return id ? { ...item, attrs: { ...item.attrs, id } } : { type: 'text', text: `@${item.attrs.label}` }
    }
    return item.content ? { ...item, content: item.content.map(resolve) } : item
  }

  return resolve(node)
}

export const resolveRoleMentions = (message, roleIds) => JSON.stringify(resolveRoleMentionNodes(JSON.parse(message), roleIds))

export default MentionExtension
